(() => {
  const marker = '[data-phone-model-option]';
  const contextSelector = '[data-variant-context], [data-high-variant-context]';
  const initialized = new WeakSet();
  const sessions = new WeakMap();
  let mainSession = null;
  let replaying = false;
  let pendingForm = null;
  const debug = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || /^192\.168\./.test(location.hostname) || new URL(location.href).searchParams.has('phone_model_debug');
  let requestSequence = 0;
  let preloadTimer;
  let preloadController;
  const completedBatches = new Set();
  let preloadRequests = 0;

  function log(stage, details, failed = false) {
    if (debug || failed) console[failed ? 'error' : 'info'](`[phone-model] ${stage} ${JSON.stringify(details)}`);
  }

  function remember(session, variants) {
    variants.filter(Boolean).forEach(variant => {
      session.variantCache.set(variant.id, variant);
      session.variantsByOptions.set(JSON.stringify(variant.options), variant);
    });
  }

  function findVariant(session, values) {
    return session.variantsByOptions.get(JSON.stringify(values));
  }

  function contextVariants(data) {
    return [data.selected, ...data.options.flatMap(option => option.values.map(value => value.variant))].filter(Boolean);
  }

  function optionScope(session) {
    return session.node.closest('.shopify-section') || session.form.parentElement || session.form;
  }

  // One in-flight selection request per form. A newer click aborts the previous
  // response so a slow availability refresh cannot repaint an older selection.
  function beginRequest(session) {
    session.selectionToken += 1;
    session.selectionController?.abort();
    const controller = new AbortController();
    session.selectionController = controller;
    const token = session.selectionToken;
    const timeout = setTimeout(() => {
      if (session.selectionToken === token) controller.abort();
    }, 15000);
    return { token, signal: controller.signal, timeout };
  }

  function isCurrent(session, token) {
    return token === session.selectionToken;
  }

  function sessionFor(form) {
    if (!form) return null;
    if (sessions.has(form)) return sessions.get(form);
    const node = form.parentElement?.querySelector(contextSelector);
    if (!node) {
      sessions.set(form, null);
      return null;
    }
    let data;
    try {
      data = JSON.parse(node.textContent);
    } catch (err) {
      sessions.set(form, null);
      return null;
    }
    const session = {
      form,
      node,
      context: data,
      variantCache: new Map(),
      variantsByOptions: new Map(),
      selectionController: null,
      selectionToken: 0,
    };
    const json = form.parentElement.querySelector('.pr_variants_json');
    if (json) remember(session, JSON.parse(json.textContent));
    remember(session, contextVariants(data));
    sessions.set(form, session);
    paintOptionState(session, data);
    return session;
  }

  async function loadContext(ids, signal) {
    const url = new URL(location.href);
    const previewTheme = window.Shopify?.theme;
    if (previewTheme?.id && previewTheme.role !== 'main') url.searchParams.set('preview_theme_id', String(previewTheme.id));
    url.searchParams.delete('variant');
    url.searchParams.delete('view');
    url.searchParams.delete('section_id');
    url.searchParams.set('option_values', ids.join(','));
    url.searchParams.set('sections', 'variant-context');
    const response = await fetch(url.toString(), { signal });
    const body = await response.text();
    if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
    const sections = JSON.parse(body);
    if (!sections['variant-context']) throw new Error('Variant section was not rendered in the requested theme');
    const doc = new DOMParser().parseFromString(sections['variant-context'], 'text/html');
    const node = doc.querySelector(contextSelector);
    if (!node) throw new Error('Response is missing variant context');
    return JSON.parse(node.textContent);
  }

  function paintOptionState(session, data) {
    if (!data?.options) return;
    const soldOutById = new Map();
    data.options.forEach(option => {
      option.values.forEach(value => soldOutById.set(String(value.id), value.available === false));
    });
    optionScope(session).querySelectorAll('[data-option-value-id]').forEach(el => {
      const soldOut = soldOutById.get(el.dataset.optionValueId);
      if (soldOut === undefined) return;
      el.classList.toggle('is--soldout', soldOut);
      el.classList.toggle('is--unavailable', soldOut);
      if (el.matches('button')) el.disabled = soldOut;
    });
  }

  async function refreshOptionState(session) {
    if (!session?.context) return;
    const groups = [...session.form.querySelectorAll('[data-swatch-option]')];
    const ids = groups.map(group => group.querySelector('[data-swatch-item].is--selected')?.dataset.optionValueId);
    if (!ids.length || ids.some(id => !id)) return;
    const request = beginRequest(session);
    try {
      const data = await loadContext(ids, request.signal);
      if (!isCurrent(session, request.token)) return;
      session.context = data;
      remember(session, contextVariants(data));
      paintOptionState(session, data);
      notifyOptions();
    } catch (err) {
      if (err.name !== 'AbortError') log('availability-failed', { message: err.message }, true);
    } finally {
      clearTimeout(request.timeout);
    }
  }

  async function requestContext(ids, values, trace, signal) {
    const started = performance.now();
    let stage = 'request';
    try {
      log('request', { ...trace, ids, values });
      stage = 'response';
      const data = await loadContext(ids, signal);
      stage = 'match-combination';
      if (!data.selected || data.selected.options.length !== values.length || !values.every((value, index) => data.selected.options[index] === value)) throw new Error('No matching variant');
      log('match', { ...trace, returnedVariant: data.selected.id, returnedValues: data.selected.options, elapsedMs: Math.round(performance.now() - started) });
      return data;
    } catch (err) {
      if (err.name !== 'AbortError') log('failed', { ...trace, stage, message: err.message, elapsedMs: Math.round(performance.now() - started) }, true);
      throw err;
    }
  }

  function notifyOptions() {
    document.querySelectorAll(marker).forEach(group => group.dispatchEvent(new Event('variant-context-updated')));
  }

  function nextBatch() {
    const session = mainSession;
    if (!session?.context?.selected) return null;
    const options = session.context.options;
    const rowIndex = options.reduce((best, option, index) => option.values.length > options[best].values.length ? index : best, 0);
    let rows = [[]];
    options.forEach((option, index) => {
      rows = rows.flatMap(row => (index === rowIndex ? [null] : option.values).map(value => [...row, value]));
      rows = rows.slice(0, 64);
    });
    for (const row of rows) {
      const key = JSON.stringify(row.map(value => value?.id ?? null));
      if (completedBatches.has(key)) continue;
      const hasMissing = options[rowIndex].values.some(value => !findVariant(session, row.map((fixed, index) => index === rowIndex ? value.name : fixed.name)));
      if (!hasMissing) { completedBatches.add(key); continue; }
      const anchor = Array.from(session.variantCache.values()).find(variant => row.every((value, index) => index === rowIndex || variant.options[index] === value.name));
      const rowValue = options[rowIndex].values.find(value => value.name === (anchor?.options[rowIndex] ?? session.context.selected.options[rowIndex]));
      if (!rowValue) { completedBatches.add(key); continue; }
      const values = row.map((fixed, index) => index === rowIndex ? rowValue : fixed);
      return { key, ids: values.map(value => value.id), values: values.map(value => value.name) };
    }
    return null;
  }

  function schedulePreload(delay = 600) {
    clearTimeout(preloadTimer);
    if (!mainSession || pendingForm || preloadController || document.hidden || navigator.connection?.saveData || preloadRequests >= 64) return;
    preloadTimer = setTimeout(preloadBatch, delay);
  }

  function prioritizeSelection() {
    clearTimeout(preloadTimer);
    preloadController?.abort();
  }

  async function preloadBatch() {
    if (pendingForm || preloadController || document.hidden || !mainSession) return;
    const batch = nextBatch();
    if (!batch) { log('preload-complete', { requests: preloadRequests, cachedVariants: mainSession.variantCache.size }); return; }
    const controller = new AbortController();
    preloadController = controller;
    preloadRequests++;
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
    try {
      const data = await requestContext(batch.ids, batch.values, { request: ++requestSequence, mode: 'background' }, controller.signal);
      if (controller.signal.aborted) return;
      remember(mainSession, contextVariants(data));
      completedBatches.add(batch.key);
      notifyOptions();
      log('preload-batch', { values: batch.values, cachedVariants: mainSession.variantCache.size });
    } catch (err) {
      if (timedOut || err.name !== 'AbortError') completedBatches.add(batch.key);
    } finally {
      clearTimeout(timeout);
      preloadController = null;
      schedulePreload();
    }
  }

  function hydrate(session, data) {
    const variants = contextVariants(data);
    remember(session, variants);
    const json = session.form.parentElement.querySelector('.pr_variants_json');
    const merged = new Map(JSON.parse(json.textContent).map(variant => [variant.id, variant]));
    const input = session.form.querySelector('select[name="id"]');
    variants.forEach(variant => {
      merged.set(variant.id, variant);
      if (input && !Array.from(input.options).some(option => option.value === String(variant.id))) {
        const option = new Option(variant.title, String(variant.id));
        option.dataset.inventoryPolicy = variant.inventory_policy || 'continue';
        input.add(option);
      }
    });
    json.textContent = JSON.stringify([...merged.values()]);
    session.form.dispatchEvent(new CustomEvent('t4s:hydrate-variants', { detail: { variants } }));
  }

  function availability(group, value) {
    const session = sessionFor(group.closest('form')) || mainSession;
    if (!session?.context?.selected) return null;
    const index = Number(group.dataset.id ?? group.dataset.optionIndex);
    const values = session.context.selected.options.slice();
    values[index] = value;
    const variant = findVariant(session, values);
    return variant ? Boolean(variant.available) : true;
  }

  function applyVariant(session, item, data) {
    hydrate(session, data);
    session.context = data;
    replaying = true;
    try {
      item.click();
      session.form.querySelector('[name="id"]').dispatchEvent(new Event('change', { bubbles: true }));
      document.querySelectorAll(marker).forEach(group => group.dispatchEvent(new Event('variant-context-updated')));
    } finally {
      replaying = false;
    }
    paintOptionState(session, data);
  }

  function selectedIds(form, item) {
    return [...form.querySelectorAll('[data-swatch-option]')].map(group => {
      const selected = group.contains(item) ? item : group.querySelector('[data-swatch-item].is--selected');
      return { value: selected?.dataset.value, id: selected?.dataset.optionValueId };
    });
  }

  document.addEventListener('click', async event => {
    if (replaying) return;
    const item = event.target.closest('[data-swatch-item]');
    const form = item?.closest('form');
    const session = sessionFor(form);
    if (!item || !session) return;
    if (pendingForm === form) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    prioritizeSelection();
    const groups = selectedIds(form, item);
    const requestedValues = groups.map(group => group.value);
    const knownVariant = findVariant(session, requestedValues);
    const trace = { request: ++requestSequence, product: form.dataset.productid, values: requestedValues, previousVariant: session.context.selected?.id, cachedVariants: session.variantCache.size };
    log('selection', { ...trace, cacheHit: Boolean(knownVariant), matchedVariant: knownVariant?.id });
    form.querySelector('[data-variant-load-error]')?.remove();
    if (knownVariant) {
      const started = performance.now();
      session.context = { ...session.context, selected: knownVariant };
      hydrate(session, { selected: knownVariant, options: [] });
      queueMicrotask(() => {
        document.querySelectorAll(marker).forEach(group => group.dispatchEvent(new Event('variant-context-updated')));
        log('cached-applied', { ...trace, variant: knownVariant.id, formVariant: form.querySelector('[name="id"]').value, elapsedMs: Math.round(performance.now() - started) });
        refreshOptionState(session);
        if (session === mainSession) schedulePreload();
      });
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const ids = groups.map(group => group.id);
    if (ids.some(id => !id)) {
      log('missing-option-id', { ...trace, ids }, true);
      if (session === mainSession) schedulePreload();
      return;
    }
    const request = beginRequest(session);
    pendingForm = form;
    form.setAttribute('aria-busy', 'true');
    const buttons = Array.from(optionScope(session).querySelectorAll('[data-atc-form], [data-action-atc], .shopify-payment-button button'));
    const states = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; });
    const started = performance.now();
    let handoff = false;
    try {
      const data = await requestContext(ids, requestedValues, trace, request.signal);
      if (!isCurrent(session, request.token)) return;
      buttons.forEach((button, index) => { button.disabled = states[index]; });
      applyVariant(session, item, data);
      log('applied', { ...trace, variant: data.selected.id, formVariant: form.querySelector('[name="id"]').value, elapsedMs: Math.round(performance.now() - started) });
    } catch (err) {
      if (!isCurrent(session, request.token) || err.name === 'AbortError') return;
      buttons.forEach((button, index) => { button.disabled = states[index]; });
      if (err.message === 'No matching variant') {
        // This option combination does not exist. Let the theme pick a
        // fallback, then refresh availability for the selection it landed on.
        replaying = true;
        try { item.click(); } finally { replaying = false; }
        handoff = true;
        return;
      }
      log('selection-failed', { ...trace, message: err.message }, true);
      const error = document.createElement('p');
      error.dataset.variantLoadError = '';
      error.setAttribute('role', 'alert');
      error.textContent = '規格載入失敗，請重新選擇。';
      form.appendChild(error);
    } finally {
      clearTimeout(request.timeout);
      if (!isCurrent(session, request.token)) return;
      replaying = false;
      pendingForm = null;
      form.removeAttribute('aria-busy');
      form.querySelector('[name="id"]').dispatchEvent(new Event('change', { bubbles: true }));
      document.querySelectorAll(marker).forEach(group => group.dispatchEvent(new Event('variant-context-updated')));
      if (session === mainSession) schedulePreload();
      if (handoff) refreshOptionState(session);
    }
  }, true);

  document.addEventListener('submit', event => {
    if (pendingForm && event.target === pendingForm) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) prioritizeSelection();
    else schedulePreload();
  });

  document.querySelectorAll('form.t4s-form__product').forEach(form => {
    const session = sessionFor(form);
    if (session && !mainSession) mainSession = session;
  });
  if (document.readyState === 'complete') schedulePreload(1500);
  else window.addEventListener('load', () => schedulePreload(1500), { once: true });

  function initialize(group) {
    if (initialized.has(group)) return;
    const source = group.querySelector('.t4s-swatch__list, .t4s-sticky-atc__option-list');
    if (!source) return;
    const items = Array.from(source.querySelectorAll('[data-swatch-item], [data-sticky-option-value]'));
    if (!items.length) return;
    initialized.add(group);
    const select = document.createElement('select');
    select.className = 'phone-model-select';
    select.setAttribute('aria-label', '手機型號');
    const placeholder = new Option('請選擇手機型號', '');
    placeholder.disabled = true;
    select.add(placeholder);
    items.forEach(item => select.add(new Option(item.textContent.trim(), item.dataset.value)));
    group.appendChild(select);

    function sync() {
      let selected = '';
      items.forEach((item, index) => {
        const available = availability(group, item.dataset.value);
        select.options[index + 1].disabled = available === null ? item.disabled || item.getAttribute('aria-disabled') === 'true' || item.classList.contains('is--unavailable') || item.classList.contains('is--soldout') : !available;
        if (available !== null && item.matches('button') && item.disabled === available) item.disabled = !available;
        if (item.classList.contains('is--selected')) selected = item.dataset.value;
      });
      select.value = selected;
    }
    select.addEventListener('change', () => {
      const item = items.find(item => item.dataset.value === select.value);
      if (item) item.click();
    });
    group.addEventListener('variant-context-updated', sync);
    new MutationObserver(sync).observe(source, {
      subtree: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled']
    });
    sync();
    group.classList.add('phone-model-ready');
  }

  document.querySelectorAll(marker).forEach(group => {
    if (!group.closest('[data-sticky-addtocart]')) initialize(group);
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-sticky-addtocart] [data-action-atc]');
    if (!button || !button.hasAttribute('data-action-delay')) return;
    const bar = button.closest('[data-sticky-addtocart]');
    bar.querySelectorAll(marker).forEach(group => {
      const ready = initialized.has(group);
      initialize(group);
      if (ready) group.dispatchEvent(new Event('variant-context-updated'));
    });
  }, true);
})();
